import { DeleteOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { gql, useMutation } from "@apollo/client";
import { useApplicationContext } from "components/Application";
import { capitalize, clone, includes, remove } from "lodash";
import numeral from "numeral";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import tw from "twin.macro";

const updateOrderRowMutation = gql`
  mutation($id: ID!, $input: UpdateOrderRowInput!) {
    updateOrderRow(id: $id, input: $input) {
      row {
        id
        item {
          id
          code
        }
        referenceItem {
          id
          code
        }
        sizeQuantities {
          id
          quantity
          size {
            id
            code
          }
        }
      }
    }
  }
`;

const deleteOrderRowMutation = gql`
  mutation($id: ID!) {
    deleteOrderRow(id: $id) {
      successful
    }
  }
`;

type CartVariantProps = {
  document: any;
  row: any;
  price: number;
  refetch: any;
  firstOfGroup: boolean;
  maxSizes: number;
  dirtyRows: string[];
  setDirtyRows: React.Dispatch<React.SetStateAction<string[]>>;
  stock: boolean;
};

export const CartVariant: React.FC<CartVariantProps> = (props) => {
  const { document, row, price, refetch, firstOfGroup, maxSizes, dirtyRows, setDirtyRows, stock } = props;
  const {
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();

  const { item, referenceItem, sizeQuantities } = row;
  const { sizeGroup } = referenceItem;
  const { sizes } = sizeGroup;

  const { i18n } = useTranslation();
  const languageSuffix = capitalize(i18n.language);

  const { register, handleSubmit, formState, reset } = useForm();
  const { isDirty } = formState;

  const [execUpdate] = useMutation(updateOrderRowMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      console.error(graphQLErrors);
    },
    onCompleted: () => {
      toast.success(i18n.language === "it" ? "Riga del carrello aggiornata!" : "Cart row updated!", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      reset();
      refetch();
    },
  });

  const [execDelete] = useMutation(deleteOrderRowMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      console.error(graphQLErrors);
    },
    onCompleted: () => {
      toast.success(i18n.language === "it" ? "Riga del carrello eliminata!" : "Cart row removed!", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      refetch();
    },
  });

  const onSubmit = (data: any) => {
    const { sizeQuantities } = data;

    let cleanSizeQuantities: any[] = [];
    sizeQuantities.forEach((sizeQuantity: any) => {
      const parsedQuantity = parseInt(sizeQuantity.quantity);
      if (parsedQuantity > 0) {
        cleanSizeQuantities.push({
          sizeId: sizeQuantity.sizeId,
          quantity: parsedQuantity,
        });
      }
    });

    const cleanData = {
      ...data,
      sizeQuantities: cleanSizeQuantities,
    };

    execUpdate({ variables: { id: row.id, input: cleanData } });
  };

  const totalQuantity = useMemo<number>(() => {
    let totalQuantity = 0;
    sizeQuantities?.forEach((sizeQuantity: any) => (totalQuantity += sizeQuantity.quantity));
    return totalQuantity;
  }, [sizeQuantities]);

  const allSizes = useMemo<any[]>(() => {
    const allSizes = clone(sizes);
    for (let i = maxSizes - sizes.length; i > 0; i--) {
      allSizes.push({ id: i, value: "", empty: true });
    }
    return allSizes;
  }, [sizes, maxSizes]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (isDirty && !includes(dirtyRows, row.id)) {
        setDirtyRows((currentDirtyRows) => {
          const dr = clone(currentDirtyRows);
          dr.push(row.id);
          return dr;
        });
      } else if (!isDirty && includes(dirtyRows, row.id)) {
        setDirtyRows((currentDirtyRows) => {
          const dr = clone(currentDirtyRows);
          return remove(dr, row.id);
        });
      }
    }
    return () => {
      mounted = false;
    };
  }, [row, isDirty, dirtyRows, setDirtyRows]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input type="hidden" name="documentId" defaultValue={document.id} ref={register} />
      <input type="hidden" name="itemId" defaultValue={item.id} ref={register} />
      <input type="hidden" name="referenceItemId" defaultValue={referenceItem.id} ref={register} />
      <table style={{ borderCollapse: "initial", borderSpacing: "5px", width: "100%" }}>
        {firstOfGroup && (
          <thead>
            <tr>
              <td style={{ minWidth: "150px" }}></td>
              <td></td>
              {allSizes.map((size: any, index: number) => (
                <td key={size.id} tw="text-center" style={{ width: "32px", height: "32px" }}>
                  {size.code}
                </td>
              ))}
              <td tw="text-right" style={{ width: "60px" }}>
                {i18n.language === "it" ? "Pz" : "Qty"}
              </td>
              <td tw="text-right" style={{ width: "180px" }}>
                {i18n.language === "it" ? "Totale riga" : "Row subtotal"}
              </td>
              <td style={{ width: "32px", height: "32px" }}></td>
              <td style={{ width: "32px", height: "32px" }}></td>
            </tr>
          </thead>
        )}
        <tbody>
          <tr key={item.id}>
            <td style={{ minWidth: "150px" }}>
              {item.color.code} - {item.color[`name${languageSuffix}`]}
            </td>
            <td>
              <div>
                <span
                  style={{
                    backgroundImage: `url(${appEndpointStore}${item.color.patch})`,
                    display: "block",
                    width: "32px",
                    height: "32px",
                    backgroundSize: "cover",
                  }}
                />
              </div>
            </td>
            {allSizes.map((size: any, index: number) => {
              if (size.empty) return <td key={size.id} style={{ width: "32px", height: "32px" }}></td>;

              let currentQuantity = 0;
              sizeQuantities?.forEach((sizeQuantity: any) => {
                if (sizeQuantity.size.id === size.id) currentQuantity = sizeQuantity.quantity;
              });

              let availability = 0;
              if (stock)
                row.item?.stocks?.forEach((stock: any) => {
                  if (stock?.size?.id === size.id && stock.quantity > 0) {
                    availability = stock.quantity;
                  }
                });

              return (
                <td key={size.id} style={{ width: "32px", height: "32px" }}>
                  <input type="hidden" name={`sizeQuantities[${index}].sizeId`} defaultValue={size.id} ref={register} />
                  <input
                    type="number"
                    onFocus={(e) => e.target.select()}
                    tw="border-default border-gray-900 text-gray-900 text-center"
                    style={{ width: "32px", height: "32px" }}
                    min={0}
                    max={!stock ? 999 : availability}
                    name={`sizeQuantities[${index}].quantity`}
                    defaultValue={currentQuantity}
                    ref={register}
                  />
                </td>
              );
            })}
            <td tw="text-right" style={{ width: "60px" }}>
              {totalQuantity}
            </td>
            <td tw="text-right" style={{ width: "180px" }}>
              {numeral(price * totalQuantity).format("$0,0.00")} {i18n.language === "it" ? "+ IVA" : ""}
            </td>
            <td style={{ width: "32px", height: "32px" }}>
              <button
                css={[
                  !isDirty && tw`border-default border-gray-500 text-gray-500 cursor-not-allowed`,
                  isDirty &&
                    tw`border-default bg-green-500 border-green-500 text-white hover:(bg-green-700 border-green-700 text-white)`,
                ]}
                style={{ width: "32px", height: "30px", paddingBottom: "2px" }}
                disabled={!isDirty}
                type="submit"
              >
                <ShoppingCartOutlined style={{ fontSize: "18px" }} />
              </button>
            </td>
            <td style={{ width: "32px", height: "32px" }}>
              <button
                onClick={() => execDelete({ variables: { id: row.id } })}
                css={[
                  !row && tw`border-default border-gray-500 text-gray-500 cursor-not-allowed`,
                  row && tw`border-default border-red-500 text-red-500 border-red-500 hover:(bg-red-500 text-white)`,
                ]}
                style={{ width: "32px", height: "30px", paddingBottom: "2px" }}
                disabled={!row}
                type="button"
              >
                <DeleteOutlined style={{ fontSize: "18px" }} />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
  );
};
